import React, { useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import StatePill from '../StatePill';
import ExportOptionsModal, { downloadIcon } from '../ExportOptionsModal';
import { locName, devicesAtSite, deviceSubLocation, formatDate } from '../../utils/locations';
import { siteDeviceHealth, SITE_DEVICE_STATE_TONE, ATTENTION_STATES } from '../../utils/siteDeviceHealth';

// One sub-location within a site — either an OWL portal project, or one created by
// hand for a portal with no API behind it.
//
// Both resolve through deviceSubLocation, the same helper the site page groups by, so
// this page cannot drift from that grouping. A portal sub-location is not editable here:
// its name and the site it sits within are owned by the portal and by Settings -> Portal
// Sync. A manual one is edited on the site page.

const DEV_COLS = '130px 90px minmax(0,1fr) 120px';

function SubLocationDetail({ sub, loc, st, getStats, lightbugLive, onBack, onOpenDevice }) {
  const site = locName(loc);

  const rows = useMemo(() => {
    return devicesAtSite(st.devices || [], loc)
      .filter(d => {
        // Matched on id for a portal project and on name for a manual one: a manual
        // sub-location is joined by name, the same way device.location already works.
        const p = deviceSubLocation(d, st);
        return p && (sub.origin === 'manual' ? p.name === sub.name : p.id === sub.id);
      })
      .map(d => {
        const live = d.deviceId ? (lightbugLive || {})[String(d.deviceId)] : null;
        return {
          device: d,
          serial: d.serial || '—',
          type: d.type || '—',
          health: siteDeviceHealth(d, getStats(d), live),
          lastSeen: live && live.lastSeen ? new Date(live.lastSeen) : null,
        };
      })
      .sort((a, b) => {
        const aA = ATTENTION_STATES.includes(a.health) ? 0 : 1;
        const bA = ATTENTION_STATES.includes(b.health) ? 0 : 1;
        if (aA !== bA) return aA - bA;
        return String(a.serial).localeCompare(String(b.serial));
      });
  }, [sub.id, sub.name, sub.origin, loc, st, getStats, lightbugLive]);

  const attention = rows.filter(r => ATTENTION_STATES.includes(r.health)).length;
  const types = useMemo(() => {
    const by = {};
    rows.forEach(r => { by[r.type] = (by[r.type] || 0) + 1; });
    return Object.entries(by).sort((a, b) => b[1] - a[1]);
  }, [rows]);

  const btnGhost = {
    flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, padding: '0 12px',
    borderRadius: 6, border: '1px solid var(--border-default)', background: 'var(--surface-default)',
    color: 'var(--text-primary)', fontFamily: 'inherit', fontSize: 14, fontWeight: 500, cursor: 'pointer',
  };

  const [exportOpen, setExportOpen] = useState(false);

  // Columns mirror the table, with the device id in front: the serial is what is read off
  // the unit, the device id is what joins back to the OWL portal.
  const exportXlsx = () => {
    const aoa = [['Device ID', 'Serial', 'Type', 'State', 'Last seen']];
    rows.forEach(r => aoa.push([
      r.device.deviceId || '',
      r.device.serial || '',
      r.type === '—' ? '' : r.type,
      r.health,
      r.lastSeen ? r.lastSeen.toLocaleDateString('en-GB') : '',
    ]));
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{ wch: 16 }, { wch: 18 }, { wch: 12 }, { wch: 16 }, { wch: 14 }];
    const wb = XLSX.utils.book_new();
    // Excel rejects sheet names over 31 chars or containing : \ / ? * [ ].
    const sheet = (sub.name || 'Devices').replace(/[:\\/?*[\]]/g, ' ').slice(0, 31) || 'Devices';
    XLSX.utils.book_append_sheet(wb, ws, sheet);
    const base = ('sub-location-' + site + '-' + sub.name)
      .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    XLSX.writeFile(wb, base + '-' + new Date().toISOString().slice(0, 10) + '.xlsx');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <ExportOptionsModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        devices={rows.map(r => r.device)}
        subtitle={`${rows.length} device${rows.length === 1 ? '' : 's'} · ${sub.name}`}
        actions={[{ label: 'Export XLSX', icon: downloadIcon, onClick: exportXlsx }]}
      />

      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <button type="button" onClick={onBack} style={{ ...btnGhost, marginTop: 2 }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.75 }}><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
          {site}
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-.3px', color: 'var(--text-primary)' }}>{sub.name}</span>
            <StatePill state={sub.origin === 'manual' ? 'Manual' : 'From the OWL portal'} tone={sub.origin === 'manual' ? 'neutral' : 'info'} />
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-muted)', marginTop: 4 }}>
            Within {site}
          </div>
        </div>
        <button type="button" onClick={() => setExportOpen(true)} style={{ ...btnGhost, marginTop: 2 }}>
          {downloadIcon}
          Export
        </button>
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div className="card" style={{ flex: '1 1 460px', minWidth: 0, padding: 0, overflow: 'hidden' }}>
          <div style={{ height: 44, display: 'flex', alignItems: 'center', gap: 10, padding: '0 16px', borderBottom: '1px solid var(--border-default)' }}>
            <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', flex: 1 }}>Devices here</span>
            {attention > 0 && <StatePill state={`${attention} need${attention === 1 ? 's' : ''} attention`} tone="warning" />}
            <span className="tnum" style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>{rows.length}</span>
          </div>

          {rows.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center' }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>No devices here yet</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 5 }}>
                {sub.origin === 'portal'
                  ? `Devices appear here once the portal assigns them to this project and they are standing on ${site}.`
                  : `Tag devices with “${sub.name}” when deploying to ${site}, or from a device’s sidebar.`}
              </div>
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: DEV_COLS, gap: '0 16px', height: 34, alignItems: 'center', padding: '0 16px', background: 'var(--surface-inset)', borderBottom: '1px solid var(--border-default)', fontSize: 11.5, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
                <div>Serial</div><div>Type</div><div>State</div><div style={{ textAlign: 'right' }}>Last seen</div>
              </div>
              {rows.map(r => (
                <div
                  key={r.device.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => onOpenDevice && onOpenDevice(r.device)}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (onOpenDevice) onOpenDevice(r.device); } }}
                  style={{ display: 'grid', gridTemplateColumns: DEV_COLS, gap: '0 16px', alignItems: 'center', height: 44, padding: '0 16px', borderBottom: '1px solid var(--border-subtle)', cursor: onOpenDevice ? 'pointer' : 'default' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-raised)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                  <div className="tnum" style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>{r.serial}</div>
                  <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>{r.type}</div>
                  <div><StatePill state={r.health} tone={SITE_DEVICE_STATE_TONE[r.health]} /></div>
                  <div className="tnum" style={{ textAlign: 'right', fontSize: 14, color: 'var(--text-muted)' }}>
                    {r.lastSeen ? r.lastSeen.toLocaleDateString('en-GB') : '—'}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        <div className="card" style={{ flex: '0 1 320px', minWidth: 280, padding: 0, overflow: 'hidden' }}>
          <div style={{ height: 44, display: 'flex', alignItems: 'center', padding: '0 16px', borderBottom: '1px solid var(--border-default)' }}>
            <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>Details</span>
          </div>
          {[
            ['Location', site],
            ['Client', loc.customer || 'Not set'],
            // Raw portal ids are not shown: they mean nothing outside the portal,
            // and the pill in the header already says where this came from.
            ...(sub.origin === 'portal' ? [] : [['Source', 'Created in OWL Bench']]),
            ['Devices', String(rows.length)],
            ['Needing attention', String(attention)],
          ].map(([label, value]) => (
            <div key={label} style={{ display: 'flex', alignItems: 'baseline', gap: 12, padding: '9px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
              <span style={{ fontSize: 12, color: 'var(--text-muted)', width: 118, flexShrink: 0 }}>{label}</span>
              <span className="tnum" style={{ fontSize: 14, color: 'var(--text-primary)', flex: 1, textAlign: 'right' }}>{value}</span>
            </div>
          ))}
          {!!types.length && (
            <>
              <div style={{ padding: '9px 16px', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', background: 'var(--surface-inset)', borderBottom: '1px solid var(--border-subtle)' }}>Device types</div>
              {types.map(([t, n]) => (
                <div key={t} style={{ display: 'flex', alignItems: 'baseline', gap: 12, padding: '9px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
                  <span style={{ fontSize: 14, color: 'var(--text-primary)', flex: 1, minWidth: 0 }}>{t}</span>
                  <span className="tnum" style={{ fontSize: 14, color: 'var(--text-muted)' }}>{n}</span>
                </div>
              ))}
            </>
          )}
          <div style={{ padding: '10px 16px', background: 'var(--surface-inset)', fontSize: 12, color: 'var(--text-muted)' }}>
            {sub.origin === 'portal' ? (
              <>
                This sub-location comes from the OWL portal. Its name and the location it sits within
                are set in Settings → Portal Sync
                {st.portalSnapshot?.fetchedAt ? `, last synced ${formatDate(st.portalSnapshot.fetchedAt)}` : ''}.
              </>
            ) : (
              <>
                Created in OWL Bench. Devices are placed here by hand — when deploying, or from a
                device&rsquo;s sidebar.
                {sub.stored?.note ? ' ' + sub.stored.note : ''}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SubLocationDetail;
